import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
    ArrowLeft,
    Check,
    Code2,
    Lock,
    Monitor,
    Plus,
    Smartphone,
    Sparkles,
    Trash2,
    Layers,
    Copy,
    Send,
    Terminal as TerminalIcon,
    Eye,
    Globe,
    Mail as MailIcon,
    AlertCircle,
    Sliders,
    X,
    Settings2,
    FlaskConical,
    ChevronRight,
    Zap,
    Tag as TagIcon,
} from 'lucide-react';

import { PageLayout } from '@/components/page-layout';
import { useLayout } from '@/contexts/layout-context';
import { useEmailTemplates } from '@/store/email-template';
import type { EmailTemplate, VariableDef } from '@/store/email-template';
import { ConfirmDeleteModal } from './confirm-delete-modal';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { http } from '@/libs/http';

// Fallback built-in system variables
const SYSTEM_VARIABLES: VariableDef[] = [
    { key: 'to', label: 'Primary Recipient', default_value: 'recipient@example.com', description: 'Target email address', required: true },
    { key: 'cc', label: 'Carbon Copy', default_value: 'cc@example.com', description: 'CC recipient addresses', required: false },
    { key: 'bcc', label: 'Blind Carbon Copy', default_value: 'bcc@example.com', description: 'BCC recipient addresses', required: false },
    { key: 'subject', label: 'Email Subject', default_value: 'Notification Update', description: 'Email subject text', required: true },
];

type ActiveTab = 'general' | 'variables' | 'triggers' | 'editor' | 'testing' | 'api';

const TABS: { id: ActiveTab; label: string; icon: React.ElementType }[] = [
    { id: 'general', label: 'General', icon: Settings2 },
    { id: 'variables', label: 'Variables', icon: Layers },
    { id: 'triggers', label: 'Event Triggers', icon: Zap },
    { id: 'editor', label: 'Editor', icon: MailIcon },
    { id: 'testing', label: 'Testing', icon: FlaskConical },
    { id: 'api', label: 'API Docs', icon: TerminalIcon },
];

export const ManageMailTemplatePage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { setBreadcrumbs } = useLayout();
    const {
        templates,
        fetchTemplates,
        updateTemplate,
        deleteTemplate,
        serviceConfigs,
        fetchServiceConfigs,
        renderPreview,
        sendTestEmail
    } = useEmailTemplates();

    const [template, setTemplate] = useState<EmailTemplate | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [activeTab, setActiveTab] = useState<ActiveTab>('general');

    // Form fields
    const [key, setKey] = useState('');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [subject, setSubject] = useState('');
    const [htmlContent, setHtmlContent] = useState('');
    const [triggerType, setTriggerType] = useState<'http' | 'event'>('http');
    const [selectedTestingEventId, setSelectedTestingEventId] = useState<string>('');
    const [serviceConfigId, setServiceConfigId] = useState<string>('');
    const [isActive, setIsActive] = useState<boolean>(true);
    const [tags, setTags] = useState<string[]>([]);
    const [newTagInput, setNewTagInput] = useState('');
    const [eventIds, setEventIds] = useState<string[]>([]);
    const [availableEvents, setAvailableEvents] = useState<any[]>([]);

    // Variables state
    const [systemVariables, setSystemVariables] = useState<VariableDef[]>(SYSTEM_VARIABLES);
    const [customVariables, setCustomVariables] = useState<VariableDef[]>([]);

    // New variable inputs
    const [newVarKey, setNewVarKey] = useState('');
    const [newVarLabel, setNewVarLabel] = useState('');
    const [newVarDefault, setNewVarDefault] = useState('');
    const [newVarRequired, setNewVarRequired] = useState(false);

    // Preview state
    const [showPreview, setShowPreview] = useState(false);
    const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
    const [renderedSubject, setRenderedSubject] = useState('');
    const [renderedHtml, setRenderedHtml] = useState('');

    // Test Send Modal
    const [showTestModal, setShowTestModal] = useState(false);
    const [testRecipient, setTestRecipient] = useState('user@example.com');
    const [testVariableValues, setTestVariableValues] = useState<Record<string, string>>({});
    const [isSendingTest, setIsSendingTest] = useState(false);
    const [copiedCurl, setCopiedCurl] = useState(false);
    const [copiedUrl, setCopiedUrl] = useState(false);
    const [copiedVarKey, setCopiedVarKey] = useState<string | null>(null);

    useEffect(() => {
        fetchTemplates();
        fetchServiceConfigs();
        http.get<any[]>('/mgmt/v1/events')
            .then(data => setAvailableEvents(data || []))
            .catch(err => console.error('Failed to fetch events', err));
    }, [fetchTemplates, fetchServiceConfigs]);

    useEffect(() => {
        if (id) {
            const found = templates.find(t => t.id === id || t.key === id);
            if (found) {
                setTemplate(found);
                setKey(found.key);
                setName(found.name);
                setDescription(found.description || '');
                setSubject(found.subject);
                setHtmlContent(found.html_content);
                setTriggerType(found.trigger_type || 'http');
                setServiceConfigId(found.service_config_id || '');
                setIsActive(found.is_active);
                setTags(found.tags || []);
                setEventIds(found.event_ids || []);
                if (found.event_ids && found.event_ids.length > 0) {
                    setSelectedTestingEventId(found.event_ids[0]);
                }

                const dbVars = found.variables || [];
                const systemKeys = ['to', 'cc', 'bcc', 'subject'];

                const loadedSystem = SYSTEM_VARIABLES.map(sysVar => {
                    const dbMatch = dbVars.find(v => v.key === sysVar.key);
                    return dbMatch ? { ...sysVar, ...dbMatch } : sysVar;
                });
                const loadedCustom = dbVars.filter(v => !systemKeys.includes(v.key));

                setSystemVariables(loadedSystem);
                setCustomVariables(loadedCustom);

                const defaults: Record<string, string> = {};
                [...loadedSystem, ...loadedCustom].forEach(v => {
                    defaults[v.key] = v.default_value || '';
                });
                setTestVariableValues(prev => ({ ...defaults, ...prev }));
                if (defaults['to']) {
                    setTestRecipient(defaults['to']);
                }

                setBreadcrumbs([
                    { name: 'Services', to: '/m/mail' },
                    { name: 'Email Templates', to: '/m/mail' },
                    { name: found.name }
                ]);
            }
        }
    }, [id, templates, setBreadcrumbs]);

    // Automatically load sample payload when target event changes or events are loaded
    useEffect(() => {
        if (availableEvents.length > 0 && selectedTestingEventId) {
            const foundEvt = availableEvents.find(item => item.event_id === selectedTestingEventId);
            if (foundEvt && foundEvt.sample_payload) {
                setTestVariableValues(prev => ({ ...foundEvt.sample_payload, ...prev }));
                if (foundEvt.sample_payload.to && (!testRecipient || testRecipient === 'user@example.com')) {
                    setTestRecipient(foundEvt.sample_payload.to);
                }
            }
        }
    }, [availableEvents, selectedTestingEventId]);

    // Derive Event Payload variables dynamically from available platform events or testVariableValues
    const eventPayloadVars = useMemo(() => {
        const relevantEvents = availableEvents.filter(e =>
            e.event_id === selectedTestingEventId || eventIds.includes(e.event_id)
        );
        const activeEvents = relevantEvents.length > 0
            ? relevantEvents
            : (triggerType === 'event' && availableEvents.length > 0 ? [availableEvents[0]] : []);

        const payloadKeysMap = new Map<string, VariableDef>();

        activeEvents.forEach(evt => {
            if (evt.sample_payload) {
                Object.entries(evt.sample_payload).forEach(([key, val]) => {
                    if (!payloadKeysMap.has(key)) {
                        const formattedLabel = key.startsWith('_')
                            ? key.slice(1).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                            : key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

                        payloadKeysMap.set(key, {
                            key,
                            label: formattedLabel,
                            default_value: String(val ?? ''),
                            description: `Event payload variable from ${evt.event_id}`,
                            required: false
                        });
                    }
                });
            }
        });

        Object.keys(testVariableValues).forEach(key => {
            if (!payloadKeysMap.has(key)) {
                const formattedLabel = key.startsWith('_')
                    ? key.slice(1).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                    : key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

                payloadKeysMap.set(key, {
                    key,
                    label: formattedLabel,
                    default_value: String(testVariableValues[key] ?? ''),
                    description: 'Event test payload variable',
                    required: false
                });
            }
        });

        return Array.from(payloadKeysMap.values());
    }, [availableEvents, selectedTestingEventId, eventIds, triggerType, testVariableValues]);

    const allVars = useMemo(() => {
        const existingKeys = new Set([...systemVariables, ...customVariables].map(v => v.key));
        const extraEventVars = eventPayloadVars.filter(v => !existingKeys.has(v.key));
        return [...systemVariables, ...customVariables, ...extraEventVars];
    }, [systemVariables, customVariables, eventPayloadVars]);

    // Live update preview
    useEffect(() => {
        const payload: Record<string, any> = { ...testVariableValues };
        allVars.forEach(v => {
            if (v.key === 'to') {
                payload[v.key] = testRecipient;
            } else if (Object.prototype.hasOwnProperty.call(testVariableValues, v.key)) {
                // If present in test payload state (even if empty string ''), use the test payload value!
                payload[v.key] = testVariableValues[v.key];
            } else {
                payload[v.key] = v.default_value || '';
            }
        });
        // Evaluate subject template: priority is testVariableValues['subject'] -> systemVariables['subject'].default_value -> subject state
        const testSubject = testVariableValues['subject'];
        const sysSubject = systemVariables.find(v => v.key === 'subject')?.default_value;
        const activeSubjectTemplate = (testSubject !== undefined && testSubject !== '') ? testSubject : (sysSubject || subject);

        renderPreview(activeSubjectTemplate, htmlContent, allVars, payload)
            .then(res => {
                setRenderedSubject(res.subject);
                setRenderedHtml(res.html_content);
            });
    }, [subject, htmlContent, systemVariables, customVariables, allVars, testRecipient, testVariableValues, renderPreview]);

    const handleCopyVar = (varKey: string) => {
        const text = `{{${varKey}}}`;
        navigator.clipboard.writeText(text);
        setCopiedVarKey(varKey);
        toast.success(`Copied ${text} to clipboard`);
        setTimeout(() => setCopiedVarKey(null), 2000);
    };

    const handleAddVariable = () => {
        if (!newVarKey.trim()) {
            toast.error('Variable key is required');
            return;
        }
        const cleanKey = newVarKey.trim().replace(/[^a-zA-Z0-9_]/g, '');
        const systemKeys = ['to', 'cc', 'bcc', 'subject'];
        if (systemKeys.includes(cleanKey) || customVariables.some(v => v.key === cleanKey)) {
            toast.error(`Variable key '${cleanKey}' already exists`);
            return;
        }
        setCustomVariables([
            ...customVariables,
            {
                key: cleanKey,
                label: newVarLabel || cleanKey,
                default_value: newVarDefault,
                description: '',
                required: newVarRequired
            }
        ]);
        if (newVarDefault) {
            setTestVariableValues(prev => ({ ...prev, [cleanKey]: newVarDefault }));
        }
        setNewVarKey('');
        setNewVarLabel('');
        setNewVarDefault('');
        setNewVarRequired(false);
        toast.success(`Added variable {{${cleanKey}}}`);
    };

    const handleRemoveVariable = (keyToRemove: string) => {
        setCustomVariables(customVariables.filter(v => v.key !== keyToRemove));
    };

    const handleUpdateVariable = (key: string, fields: Partial<VariableDef>) => {
        setCustomVariables(customVariables.map(v =>
            v.key === key ? { ...v, ...fields } : v
        ));
        if (fields.default_value !== undefined) {
            setTestVariableValues(prev => ({ ...prev, [key]: fields.default_value! }));
        }
    };

    const handleUpdateSystemVariable = (key: string, fields: Partial<VariableDef>) => {
        setSystemVariables(systemVariables.map(v =>
            v.key === key ? { ...v, ...fields } : v
        ));
        if (key === 'subject' && fields.default_value !== undefined) {
            setSubject(fields.default_value);
        }
        if (fields.default_value !== undefined) {
            setTestVariableValues(prev => ({ ...prev, [key]: fields.default_value! }));
        }
    };

    const handleToggleRequired = (keyToToggle: string) => {
        setCustomVariables(customVariables.map(v =>
            v.key === keyToToggle ? { ...v, required: !v.required } : v
        ));
    };

    const invocationUrl = `${window.location.origin}/api/svc/v1/email/${key || '<template_id>'}/send`;

    const handleCopyUrl = () => {
        navigator.clipboard.writeText(invocationUrl);
        setCopiedUrl(true);
        toast.success('Invocation URL copied');
        setTimeout(() => setCopiedUrl(false), 2000);
    };

    const handleSave = async () => {
        if (!id) return;
        setIsSaving(true);
        try {
            await updateTemplate(id, {
                key,
                name,
                description,
                subject,
                html_content: htmlContent,
                variables: [...systemVariables, ...customVariables],
                tags,
                event_ids: eventIds,
                trigger_type: triggerType,
                service_config_id: serviceConfigId || null,
                is_active: isActive
            });
        } catch (err) {
            console.error(err);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!id) return;
        try {
            await deleteTemplate(id);
            toast.success('Template deleted successfully');
            navigate('/m/mail');
        } catch (err) {
            console.error(err);
        }
    };

    const handleSendTest = async () => {
        if (!key) return;
        setIsSendingTest(true);
        try {
            const payload: Record<string, any> = { ...testVariableValues };
            allVars.forEach(v => {
                payload[v.key] = v.key === 'to' ? testRecipient : (testVariableValues[v.key] ?? (v.default_value || ''));
            });
            const res = await sendTestEmail(
                key,
                testRecipient,
                payload,
                serviceConfigId || undefined,
                subject,
                htmlContent
            );
            if (res.status === 'success') {
                setShowTestModal(false);
            }
        } catch (err: any) {
            // Error handled by toast inside store
        } finally {
            setIsSendingTest(false);
        }
    };

    const generateCurlSnippet = () => {
        const sampleVars: Record<string, string> = {};
        allVars.filter(v => v.key !== 'to' && v.key !== 'subject').forEach(v => {
            sampleVars[v.key] = testVariableValues[v.key] ?? (v.default_value || 'sample_value');
        });

        return `curl -X POST "${invocationUrl}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer <YOUR_ACCESS_TOKEN>" \\
  -d '{
    "to": "${testRecipient || 'user@example.com'}",
    "variables": ${JSON.stringify(sampleVars, null, 6).replace(/\n/g, '\n    ')}
  }'`;
    };

    const handleCopyCurl = () => {
        navigator.clipboard.writeText(generateCurlSnippet());
        setCopiedCurl(true);
        toast.success('CURL command copied to clipboard');
        setTimeout(() => setCopiedCurl(false), 2000);
    };

    const allMandatoryFilled = [...systemVariables, ...customVariables]
        .filter(v => v.required)
        .every(v => {
            const val = v.key === 'to' ? testRecipient : testVariableValues[v.key];
            return val && val.trim() !== '';
        });

    if (!template) {
        return (
            <PageLayout title="Manage Email Template" description="Loading template details...">
                <div className="flex items-center justify-center py-16">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-(--primary)"></div>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout
            title={template.name}
            description={`Key: ${template.key} · Service: ${template.service_config?.name || 'Default Service'}`}
            action={
                <div className="flex items-center gap-3">
                    <Link
                        to="/m/mail"
                        className="flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back
                    </Link>
                    <button
                        type="button"
                        onClick={() => setShowDeleteModal(true)}
                        className="flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-red-600 hover:text-red-700 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 rounded-lg transition-colors"
                    >
                        <Trash2 className="w-4 h-4" /> Delete
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={isSaving}
                        className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-(--primary) hover:bg-(--primary-hover) disabled:opacity-50 rounded-lg transition-colors shadow-sm"
                    >
                        <Check className="w-4 h-4" /> {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            }
        >
            <div className="animate-in fade-in duration-300">
                {/* Two-column sticky layout */}
                <div className="flex gap-6 items-start">

                    {/* ── LEFT SIDEBAR (sticky) ─────────────────────────────────── */}
                    <aside className="w-64 shrink-0 sticky top-6 space-y-4">

                        {/* Status & Identity */}
                        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 space-y-3">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-sky-50 dark:bg-sky-950/50 text-(--primary) flex items-center justify-center shrink-0">
                                    <MailIcon className="w-4 h-4" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-xs font-bold text-slate-900 dark:text-white truncate">{name || template.name}</p>
                                    <p className="text-[11px] text-slate-400 font-mono truncate">{key || template.key}</p>
                                </div>
                            </div>

                            <div className="flex items-center justify-between pt-1 border-t border-slate-100 dark:border-slate-800">
                                <span className="text-xs text-slate-500">Status</span>
                                <button
                                    type="button"
                                    onClick={() => setIsActive(!isActive)}
                                    className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full transition-colors cursor-pointer ${isActive
                                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400'
                                        : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                                        }`}
                                >
                                    <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                                    {isActive ? 'Active' : 'Disabled'}
                                </button>
                            </div>

                            <div className="pt-1 border-t border-slate-100 dark:border-slate-800">
                                <p className="text-[11px] text-slate-400 mb-1">{allVars.length} variables</p>
                            </div>
                        </div>

                        {/* Variable Quick Reference */}
                        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 space-y-2.5">
                            <div className="flex items-center gap-2">
                                <Layers className="w-3.5 h-3.5 text-(--primary)" />
                                <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300">Variable Reference</h4>
                            </div>
                            <p className="text-[11px] text-slate-400">Click to copy template tag</p>
                            <div className="space-y-1 max-h-64 overflow-y-auto">
                                {allVars.map(v => (
                                    <button
                                        key={v.key}
                                        type="button"
                                        onClick={() => handleCopyVar(v.key)}
                                        className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-800 hover:border-(--primary)/30 hover:bg-sky-50 dark:hover:bg-sky-950/20 transition-colors group"
                                    >
                                        <span className="font-mono text-[11px] text-(--primary) font-semibold truncate">{`{{${v.key}}}`}</span>
                                        {copiedVarKey === v.key
                                            ? <Check className="w-3 h-3 text-emerald-500 shrink-0" />
                                            : <Copy className="w-3 h-3 text-slate-300 group-hover:text-(--primary) shrink-0 transition-colors" />
                                        }
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Quick Actions */}
                        {/* <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 space-y-2">
                            <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Quick Actions</h4>
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={isSaving}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2 text-xs font-semibold text-white bg-(--primary) hover:bg-(--primary-hover) disabled:opacity-50 rounded-lg transition-colors shadow-sm"
                            >
                                <Check className="w-3.5 h-3.5" />
                                {isSaving ? 'Saving...' : 'Save Changes'}
                            </button>
                            <button
                                type="button"
                                onClick={() => { setActiveTab('testing'); }}
                                disabled={!allMandatoryFilled}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 rounded-lg transition-colors"
                            >
                                <Send className="w-3.5 h-3.5" />
                                Send Test Email
                            </button>
                        </div> */}
                    </aside>

                    {/* ── RIGHT CONTENT PANEL ───────────────────────────────────── */}
                    <div className="flex-1 min-w-0 space-y-4">

                        {/* Tab Navigation */}
                        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
                            <div className="flex items-center border-b border-slate-100 dark:border-slate-800 px-4">
                                {TABS.filter(tab => tab.id !== 'triggers' || triggerType === 'event').map(tab => {
                                    const Icon = tab.icon;
                                    const isActive = activeTab === tab.id;
                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            className={`flex items-center gap-2 px-3 py-3.5 text-[13px] font-semibold relative transition-colors ${isActive
                                                ? 'text-slate-900 dark:text-white'
                                                : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                                                }`}
                                        >
                                            <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-(--primary)' : ''}`} />
                                            {tab.label}
                                            {isActive && (
                                                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-(--primary) rounded-t-full" />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* ─── TAB: GENERAL ─────────────────────────────────── */}
                            {activeTab === 'general' && (
                                <div className="p-6 space-y-6 animate-in fade-in duration-200">
                                    <div className="flex items-center gap-2.5">
                                        <Sparkles className="w-4 h-4 text-(--primary)" />
                                        <h3 className="text-base font-bold text-slate-900 dark:text-white">General Configuration</h3>
                                    </div>

                                    {/* Trigger Type Selection Card */}
                                    <div className="space-y-2">
                                        <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                                            Trigger Source Mode <span className="text-red-500">*</span>
                                        </label>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div
                                                onClick={() => setTriggerType('http')}
                                                className={`p-3.5 rounded-xl border cursor-pointer transition-all flex items-start gap-3 ${triggerType === 'http'
                                                    ? 'bg-sky-50/70 dark:bg-sky-950/30 border-(--primary) ring-1 ring-(--primary)/50'
                                                    : 'bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 hover:border-slate-300'
                                                    }`}
                                            >
                                                <div className={`w-4 h-4 rounded-full border-2 mt-0.5 flex items-center justify-center shrink-0 ${triggerType === 'http' ? 'border-(--primary) bg-(--primary)' : 'border-slate-300 dark:border-slate-700'}`}>
                                                    {triggerType === 'http' && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-1.5">
                                                        <Globe className="w-3.5 h-3.5 text-sky-500" />
                                                        <p className="text-xs font-bold text-slate-900 dark:text-white">HTTP API Invocation</p>
                                                    </div>
                                                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                                                        Triggered via direct REST API POST endpoint calls with custom HTTP body variables.
                                                    </p>
                                                </div>
                                            </div>

                                            <div
                                                onClick={() => setTriggerType('event')}
                                                className={`p-3.5 rounded-xl border cursor-pointer transition-all flex items-start gap-3 ${triggerType === 'event'
                                                    ? 'bg-sky-50/70 dark:bg-sky-950/30 border-(--primary) ring-1 ring-(--primary)/50'
                                                    : 'bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 hover:border-slate-300'
                                                    }`}
                                            >
                                                <div className={`w-4 h-4 rounded-full border-2 mt-0.5 flex items-center justify-center shrink-0 ${triggerType === 'event' ? 'border-(--primary) bg-(--primary)' : 'border-slate-300 dark:border-slate-700'}`}>
                                                    {triggerType === 'event' && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-1.5">
                                                        <Zap className="w-3.5 h-3.5 text-amber-500" />
                                                        <p className="text-xs font-bold text-slate-900 dark:text-white">Platform Event Triggers</p>
                                                    </div>
                                                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                                                        Automated dispatch when platform events (e.g. <code>user.created</code>, <code>tenant.created</code>) occur.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div className="space-y-1.5">
                                            <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                                                Template Key Slug <span className="text-red-500">*</span>
                                            </label>
                                            <p className="text-[11px] text-slate-400">Used in the API endpoint URL</p>
                                            <input
                                                type="text"
                                                value={key}
                                                onChange={e => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                                                className="w-full px-3.5 py-2.5 text-sm bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-(--primary)/30 font-mono text-slate-900 dark:text-white"
                                                required
                                            />
                                        </div>

                                        <div className="space-y-1.5">
                                            <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                                                Display Name <span className="text-red-500">*</span>
                                            </label>
                                            <p className="text-[11px] text-slate-400">Human-friendly label shown in the UI</p>
                                            <input
                                                type="text"
                                                value={name}
                                                onChange={e => setName(e.target.value)}
                                                className="w-full px-3.5 py-2.5 text-sm bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-(--primary)/30 text-slate-900 dark:text-white"
                                                required
                                            />
                                        </div>

                                        <div className="space-y-1.5 md:col-span-2">
                                            <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">Description</label>
                                            <p className="text-[11px] text-slate-400">Brief description of what this template is used for</p>
                                            <input
                                                type="text"
                                                value={description}
                                                onChange={e => setDescription(e.target.value)}
                                                className="w-full px-3.5 py-2.5 text-sm bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-(--primary)/30 text-slate-900 dark:text-white"
                                            />
                                        </div>

                                        <div className="space-y-1.5 md:col-span-2">
                                            <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">Template Tags</label>
                                            <p className="text-[11px] text-slate-400">Add tags (e.g. internal, external, event) to organize templates</p>
                                            <div className="p-3 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl flex items-center gap-2 flex-wrap">
                                                {tags.map(t => (
                                                    <span key={t} className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300">
                                                        <TagIcon className="w-3 h-3" />
                                                        {t}
                                                        <button
                                                            type="button"
                                                            onClick={() => setTags(tags.filter(tag => tag !== t))}
                                                            className="hover:text-red-500 ml-0.5"
                                                        >
                                                            &times;
                                                        </button>
                                                    </span>
                                                ))}
                                                <input
                                                    type="text"
                                                    value={newTagInput}
                                                    onChange={e => setNewTagInput(e.target.value)}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter' && newTagInput.trim()) {
                                                            e.preventDefault();
                                                            const tagClean = newTagInput.trim().toLowerCase();
                                                            if (!tags.includes(tagClean)) {
                                                                setTags([...tags, tagClean]);
                                                            }
                                                            setNewTagInput('');
                                                        }
                                                    }}
                                                    placeholder="Type tag & press Enter..."
                                                    className="px-2.5 py-1 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-(--primary)"
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-1.5">
                                            <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                                                Service Connection
                                            </label>
                                            <p className="text-[11px] text-slate-400">Logic App or SMTP provider to route through</p>
                                            <select
                                                value={serviceConfigId}
                                                onChange={e => setServiceConfigId(e.target.value)}
                                                className="w-full px-3.5 py-2.5 text-sm bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-(--primary)/30 text-slate-900 dark:text-white"
                                            >
                                                <option value="">Default Email Service (Fallback)</option>
                                                {serviceConfigs.map(cfg => (
                                                    <option key={cfg.id} value={cfg.id}>
                                                        {cfg.name} ({cfg.service_type})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="space-y-1.5">
                                            <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">Template Status</label>
                                            <p className="text-[11px] text-slate-400">Disabled templates reject all invocation requests</p>
                                            <button
                                                type="button"
                                                onClick={() => setIsActive(!isActive)}
                                                className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg border transition-colors ${isActive
                                                    ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900/50'
                                                    : 'bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800'
                                                    }`}
                                            >
                                                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${isActive ? 'bg-(--primary) border-(--primary)' : 'border-slate-300 dark:border-slate-600'}`}>
                                                    {isActive && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                                                </div>
                                                <span className={`text-sm font-semibold ${isActive ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-600 dark:text-slate-400'}`}>
                                                    {isActive ? 'Enabled' : 'Disabled'}
                                                </span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Invocation Banner */}
                                    {isActive ? (
                                        <div className="p-4 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/50 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                            <div className="flex items-center gap-3">
                                                <Globe className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                                                <div>
                                                    <p className="text-[11px] font-bold text-emerald-900 dark:text-emerald-300 uppercase tracking-wider">Active Invocation Endpoint</p>
                                                    <p className="text-xs font-mono text-emerald-800 dark:text-emerald-400 mt-0.5 select-all break-all">POST {invocationUrl}</p>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handleCopyUrl}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors shrink-0"
                                            >
                                                {copiedUrl ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                                {copiedUrl ? 'Copied!' : 'Copy URL'}
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="p-3.5 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/50 rounded-xl flex items-center gap-2 text-xs text-amber-800 dark:text-amber-300 font-medium">
                                            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                                            This template is disabled. Enable it to activate the invocation endpoint.
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ─── TAB: EVENT TRIGGERS ──────────────────────────── */}
                            {activeTab === 'triggers' && (
                                <div className="p-6 space-y-6 animate-in fade-in duration-200">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2.5">
                                            <Zap className="w-4 h-4 text-(--primary)" />
                                            <div>
                                                <h3 className="text-base font-bold text-slate-900 dark:text-white">Connected Event Triggers</h3>
                                                <p className="text-xs text-slate-400 mt-0.5">Attach this email template to platform events. When an event fires, this template will be dispatched.</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Available Events Selector Grid */}
                                    <div className="space-y-3">
                                        <h4 className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Select Platform Event Triggers</h4>
                                        <div className="space-y-2">
                                            {availableEvents.length === 0 ? (
                                                <p className="text-xs text-slate-400 py-4 text-center">Loading platform events...</p>
                                            ) : (
                                                availableEvents.map(evt => {
                                                    const isSelected = eventIds.includes(evt.event_id);
                                                    return (
                                                        <div
                                                            key={evt.event_id}
                                                            onClick={() => {
                                                                if (isSelected) {
                                                                    setEventIds(eventIds.filter(id => id !== evt.event_id));
                                                                } else {
                                                                    setEventIds([...eventIds, evt.event_id]);
                                                                }
                                                            }}
                                                            className={`p-3.5 rounded-xl border transition-all cursor-pointer flex items-center justify-between gap-4 ${isSelected
                                                                ? 'bg-sky-50/70 dark:bg-sky-950/30 border-(--primary) shadow-2xs'
                                                                : 'bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 hover:border-slate-300'
                                                                }`}
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isSelected ? 'bg-(--primary) border-(--primary)' : 'border-slate-300 dark:border-slate-700'}`}>
                                                                    {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                                                                </div>
                                                                <div>
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="font-mono text-xs font-bold text-slate-900 dark:text-white">{evt.event_id}</span>
                                                                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 uppercase">
                                                                            {evt.level}
                                                                        </span>
                                                                    </div>
                                                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{evt.description}</p>
                                                                </div>
                                                            </div>

                                                            {/* Action button to load sample payload into test preview */}
                                                            {isSelected && evt.sample_payload && (
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setTestVariableValues(prev => ({ ...prev, ...evt.sample_payload }));
                                                                        setActiveTab('testing');
                                                                        toast.success(`Loaded sample payload for ${evt.event_id}`);
                                                                    }}
                                                                    className="px-3 py-1 text-xs font-semibold text-(--primary) bg-white dark:bg-slate-900 border border-sky-200 dark:border-sky-800 hover:bg-sky-50 rounded-lg transition-colors shrink-0 flex items-center gap-1.5"
                                                                >
                                                                    <Eye className="w-3 h-3" /> Load Sample & Preview
                                                                </button>
                                                            )}
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ─── TAB: VARIABLES ───────────────────────────────── */}
                            {activeTab === 'variables' && (
                                <div className="p-6 space-y-6 animate-in fade-in duration-200">
                                    {/* System Variables */}
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2">
                                            <Lock className="w-3.5 h-3.5 text-amber-500" />
                                            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                                System Built-in Variables
                                            </h4>
                                            <span className="text-[11px] text-slate-400 font-normal ml-1">— key is fixed; label & default are editable</span>
                                        </div>

                                        <div className="space-y-2">
                                            {systemVariables.map(sysVar => (
                                                <div
                                                    key={sysVar.key}
                                                    className="p-3.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
                                                >
                                                    <div className="flex items-center gap-3 flex-wrap">
                                                        <span className="font-mono font-bold text-xs text-(--primary) w-24 shrink-0">
                                                            {`{{${sysVar.key}}}`}
                                                        </span>

                                                        <div className="flex items-center gap-1.5 text-xs text-slate-500 min-w-0">
                                                            <span className="font-semibold shrink-0">Label:</span>
                                                            <input
                                                                type="text"
                                                                value={sysVar.label || ''}
                                                                onChange={e => handleUpdateSystemVariable(sysVar.key, { label: e.target.value })}
                                                                className="px-2.5 py-1 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-800 dark:text-slate-200 w-36 focus:outline-none focus:ring-1 focus:ring-(--primary)"
                                                                placeholder="Label..."
                                                            />
                                                        </div>

                                                        <div className="flex items-center gap-1.5 text-xs text-slate-500 min-w-0">
                                                            <span className="font-semibold shrink-0">Default:</span>
                                                            <input
                                                                type="text"
                                                                value={sysVar.default_value || ''}
                                                                onChange={e => handleUpdateSystemVariable(sysVar.key, { default_value: e.target.value })}
                                                                className="px-2.5 py-1 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-800 dark:text-slate-200 w-44 font-mono focus:outline-none focus:ring-1 focus:ring-(--primary)"
                                                                placeholder="Default value..."
                                                            />
                                                        </div>

                                                        <div className="ml-auto flex items-center gap-2 shrink-0">
                                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${sysVar.required ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400' : 'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400'}`}>
                                                                {sysVar.required ? 'Required' : 'Optional'}
                                                            </span>
                                                            <span className="text-[10px] bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
                                                                <Lock className="w-2.5 h-2.5" /> Built-in
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleCopyVar(sysVar.key)}
                                                                className="p-1.5 text-slate-400 hover:text-(--primary) hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-colors"
                                                                title={`Copy {{${sysVar.key}}}`}
                                                            >
                                                                {copiedVarKey === sysVar.key ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Custom Variables */}
                                    <div className="space-y-4 pt-2 border-t border-slate-100 dark:border-slate-800">
                                        <div className="flex items-center justify-between">
                                            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                                Custom Variables ({customVariables.length})
                                            </h4>
                                        </div>

                                        {/* Add Variable Row */}
                                        <div className="p-3.5 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 flex items-center gap-3 flex-wrap">
                                            <input
                                                type="text"
                                                placeholder="key (e.g. user_name)"
                                                value={newVarKey}
                                                onChange={e => setNewVarKey(e.target.value)}
                                                className="px-3 py-1.5 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white font-mono w-36"
                                            />
                                            <input
                                                type="text"
                                                placeholder="Label (e.g. User Name)"
                                                value={newVarLabel}
                                                onChange={e => setNewVarLabel(e.target.value)}
                                                className="px-3 py-1.5 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white w-40"
                                            />
                                            <input
                                                type="text"
                                                placeholder="Default value"
                                                value={newVarDefault}
                                                onChange={e => setNewVarDefault(e.target.value)}
                                                className="px-3 py-1.5 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white w-40"
                                            />
                                            <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={newVarRequired}
                                                    onChange={e => setNewVarRequired(e.target.checked)}
                                                    className="w-3.5 h-3.5 text-(--primary) rounded"
                                                />
                                                Required
                                            </label>
                                            <button
                                                type="button"
                                                onClick={handleAddVariable}
                                                className="px-4 py-1.5 text-xs font-semibold bg-(--primary) hover:bg-(--primary-hover) text-white rounded-lg transition-colors flex items-center gap-1.5 ml-auto"
                                            >
                                                <Plus className="w-3.5 h-3.5" /> Add Variable
                                            </button>
                                        </div>

                                        {/* Custom Variable List */}
                                        <div className="space-y-2">
                                            {customVariables.length === 0 ? (
                                                <div className="text-center py-8 border border-dashed border-slate-200 dark:border-slate-800 rounded-xl">
                                                    <Layers className="w-7 h-7 mx-auto text-slate-300 dark:text-slate-700 mb-2" />
                                                    <p className="text-xs text-slate-400">No custom variables yet. Add one above.</p>
                                                </div>
                                            ) : (
                                                customVariables.map(varDef => (
                                                    <div
                                                        key={varDef.key}
                                                        className="p-3.5 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
                                                    >
                                                        <div className="flex items-center gap-3 flex-wrap">
                                                            <span className="font-mono font-bold text-xs text-(--primary) w-24 shrink-0">
                                                                {`{{${varDef.key}}}`}
                                                            </span>

                                                            <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                                                <span className="font-semibold">Label:</span>
                                                                <input
                                                                    type="text"
                                                                    value={varDef.label || ''}
                                                                    onChange={e => handleUpdateVariable(varDef.key, { label: e.target.value })}
                                                                    className="px-2.5 py-1 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-800 dark:text-slate-200 w-36 focus:outline-none focus:ring-1 focus:ring-(--primary)"
                                                                    placeholder="Label..."
                                                                />
                                                            </div>

                                                            <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                                                <span className="font-semibold">Default:</span>
                                                                <input
                                                                    type="text"
                                                                    value={varDef.default_value || ''}
                                                                    onChange={e => handleUpdateVariable(varDef.key, { default_value: e.target.value })}
                                                                    className="px-2.5 py-1 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-800 dark:text-slate-200 w-44 font-mono focus:outline-none focus:ring-1 focus:ring-(--primary)"
                                                                    placeholder="Default value..."
                                                                />
                                                            </div>

                                                            <div className="ml-auto flex items-center gap-2 shrink-0">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleToggleRequired(varDef.key)}
                                                                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full transition-colors cursor-pointer ${varDef.required
                                                                        ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400'
                                                                        : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                                                                        }`}
                                                                >
                                                                    {varDef.required ? 'Required' : 'Optional'}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleCopyVar(varDef.key)}
                                                                    className="p-1.5 text-slate-400 hover:text-(--primary) hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                                                    title={`Copy {{${varDef.key}}}`}
                                                                >
                                                                    {copiedVarKey === varDef.key ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveVariable(varDef.key)}
                                                                    className="p-1.5 text-slate-400 hover:text-red-600 transition-colors rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30"
                                                                    title="Remove Variable"
                                                                >
                                                                    <Trash2 className="w-3.5 h-3.5" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>

                                    {/* Event Payload Variables (when connected or triggerType === 'event') */}
                                    {(triggerType === 'event' || eventPayloadVars.length > 0) && (
                                        <div className="space-y-4 pt-2 border-t border-slate-100 dark:border-slate-800">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <Zap className="w-3.5 h-3.5 text-amber-500" />
                                                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                                        Platform Event Payload Variables ({eventPayloadVars.length})
                                                    </h4>
                                                </div>
                                                <span className="text-[11px] text-slate-400 font-normal">
                                                    — Auto-populated from platform event payloads
                                                </span>
                                            </div>

                                            <div className="space-y-2">
                                                {eventPayloadVars.length === 0 ? (
                                                    <div className="text-center py-6 border border-dashed border-sky-200 dark:border-sky-900 bg-sky-50/50 dark:bg-sky-950/20 rounded-xl">
                                                        <Zap className="w-6 h-6 mx-auto text-amber-400 mb-1.5" />
                                                        <p className="text-xs text-slate-500">Select an event trigger in the Event Triggers tab or Test Payload tab to auto-discover schema variables.</p>
                                                    </div>
                                                ) : (
                                                    eventPayloadVars.map(evtVar => (
                                                        <div
                                                            key={evtVar.key}
                                                            className="p-3.5 bg-sky-50/40 dark:bg-sky-950/20 border border-sky-200/70 dark:border-sky-900/40 rounded-xl"
                                                        >
                                                            <div className="flex items-center gap-3 flex-wrap">
                                                                <span className="font-mono font-bold text-xs text-(--primary) w-28 shrink-0">
                                                                    {`{{${evtVar.key}}}`}
                                                                </span>

                                                                <div className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                                                                    <span className="font-semibold text-slate-500">Label:</span>
                                                                    <span>{evtVar.label}</span>
                                                                </div>

                                                                <div className="flex items-center gap-1.5 text-xs text-slate-500 font-mono">
                                                                    <span className="font-semibold text-slate-500 font-sans">Sample Value:</span>
                                                                    <span className="bg-white dark:bg-slate-900 px-2 py-0.5 rounded border border-slate-200 dark:border-slate-800 truncate max-w-xs">
                                                                        {testVariableValues[evtVar.key] || evtVar.default_value || 'N/A'}
                                                                    </span>
                                                                </div>

                                                                <div className="ml-auto flex items-center gap-2 shrink-0">
                                                                    <span className="text-[10px] bg-sky-100 dark:bg-sky-900 text-sky-700 dark:text-sky-300 font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
                                                                        <Zap className="w-2.5 h-2.5 text-amber-500" /> Event Payload
                                                                    </span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleCopyVar(evtVar.key)}
                                                                        className="p-1.5 text-slate-400 hover:text-(--primary) hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-colors"
                                                                        title={`Copy {{${evtVar.key}}}`}
                                                                    >
                                                                        {copiedVarKey === evtVar.key ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ─── TAB: EDITOR ──────────────────────────────────── */}
                            {activeTab === 'editor' && (
                                <div className="animate-in fade-in duration-200">
                                    {/* Editor toolbar */}
                                    <div className="px-5 py-3 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <Code2 className="w-4 h-4 text-(--primary)" />
                                            <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                                                {showPreview ? 'Email Preview' : 'HTML Editor'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setShowPreview(!showPreview)}
                                                className={`flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold rounded-lg transition-colors ${showPreview
                                                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                                    : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 hover:bg-slate-50'
                                                    }`}
                                            >
                                                <Eye className="w-3.5 h-3.5" />
                                                {showPreview ? 'Switch to Editor' : 'Preview'}
                                            </button>
                                            {showPreview && (
                                                <div className="flex items-center gap-1 p-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg">
                                                    <button
                                                        type="button"
                                                        onClick={() => setPreviewDevice('desktop')}
                                                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${previewDevice === 'desktop' ? 'bg-slate-100 dark:bg-slate-700 text-(--primary) font-bold' : 'text-slate-400'}`}
                                                    >
                                                        <Monitor className="w-3.5 h-3.5" /> Desktop
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setPreviewDevice('mobile')}
                                                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${previewDevice === 'mobile' ? 'bg-slate-100 dark:bg-slate-700 text-(--primary) font-bold' : 'text-slate-400'}`}
                                                    >
                                                        <Smartphone className="w-3.5 h-3.5" /> Mobile
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="p-5">
                                        {showPreview ? (
                                            /* PREVIEW MODE */
                                            <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden bg-white dark:bg-slate-950 shadow-sm">
                                                {/* Email Header */}
                                                <div className="p-5 border-b border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/90 space-y-3">
                                                    <h3 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
                                                        {renderedSubject || '(No Subject)'}
                                                    </h3>
                                                    <div className="space-y-2 pt-1 border-t border-slate-200/60 dark:border-slate-800 text-xs">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-bold text-slate-400 w-16 uppercase tracking-wider">To:</span>
                                                            <span className="font-mono text-slate-900 dark:text-white bg-slate-200/60 dark:bg-slate-800 px-2 py-0.5 rounded font-medium">
                                                                {testRecipient || 'recipient@example.com'}
                                                            </span>
                                                        </div>
                                                        {testVariableValues['cc']?.trim() && (
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-bold text-slate-400 w-16 uppercase tracking-wider">Cc:</span>
                                                                <span className="font-mono text-slate-600 dark:text-slate-300">{testVariableValues['cc']}</span>
                                                            </div>
                                                        )}
                                                        {testVariableValues['bcc']?.trim() && (
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-bold text-slate-400 w-16 uppercase tracking-wider">Bcc:</span>
                                                                <span className="font-mono text-slate-600 dark:text-slate-300">{testVariableValues['bcc']}</span>
                                                            </div>
                                                        )}
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-bold text-slate-400 w-16 uppercase tracking-wider">Date:</span>
                                                            <span className="text-slate-500">
                                                                {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* iframe body */}
                                                <div className={`mx-auto h-[480px] flex flex-col transition-all ${previewDevice === 'mobile' ? 'max-w-sm border-x border-slate-300 shadow-md' : 'w-full'}`}>
                                                    <iframe
                                                        title="Email Body Preview"
                                                        srcDoc={renderedHtml}
                                                        className="w-full flex-1 border-0 bg-white"
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            /* EDITOR MODE */
                                            <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden bg-white dark:bg-slate-950">
                                                {/* To */}
                                                <div className="flex items-center px-4 py-2.5 border-b border-slate-100 dark:border-slate-800 text-xs">
                                                    <span className="w-16 font-bold text-slate-500 uppercase tracking-wider">To:</span>
                                                    <span className="font-mono bg-slate-100 dark:bg-slate-900 text-(--primary) px-2 py-1 rounded font-semibold">
                                                        {`{{to}}`}
                                                    </span>
                                                    <span className="text-[11px] text-slate-400 ml-3">(Primary recipient variable)</span>
                                                </div>
                                                {/* Cc */}
                                                <div className="flex items-center px-4 py-2.5 border-b border-slate-100 dark:border-slate-800 text-xs">
                                                    <span className="w-16 font-bold text-slate-500 uppercase tracking-wider">Cc:</span>
                                                    <span className="font-mono bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-300 px-2 py-1 rounded">{`{{cc}}`}</span>
                                                </div>
                                                {/* Bcc */}
                                                <div className="flex items-center px-4 py-2.5 border-b border-slate-100 dark:border-slate-800 text-xs">
                                                    <span className="w-16 font-bold text-slate-500 uppercase tracking-wider">Bcc:</span>
                                                    <span className="font-mono bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-300 px-2 py-1 rounded">{`{{bcc}}`}</span>
                                                </div>
                                                {/* Subject */}
                                                <div className="flex items-center px-4 py-2.5 border-b border-slate-200 dark:border-slate-800 text-xs">
                                                    <span className="w-16 font-bold text-slate-500 uppercase tracking-wider">Subject:</span>
                                                    <span className="font-mono bg-slate-100 dark:bg-slate-900 text-(--primary) px-2 py-1 rounded font-semibold">
                                                        {`{{subject}}`}
                                                    </span>
                                                    <span className="text-[11px] text-slate-400 ml-3 font-mono truncate">
                                                        ({subject || 'Default: Notification Update'})
                                                    </span>
                                                </div>
                                                {/* HTML Body */}
                                                <div className="p-4 space-y-2">
                                                    <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
                                                        <span className="flex items-center gap-1.5">
                                                            <Code2 className="w-3.5 h-3.5 text-(--primary)" /> HTML Body
                                                        </span>
                                                        <span className="text-slate-400 font-normal">Paste or edit HTML content directly</span>
                                                    </div>
                                                    <textarea
                                                        rows={18}
                                                        value={htmlContent}
                                                        onChange={e => setHtmlContent(e.target.value)}
                                                        className="w-full p-4 bg-slate-950 text-slate-100 font-mono text-xs rounded-xl border border-slate-800 focus:outline-none focus:ring-2 focus:ring-(--primary)/40 leading-relaxed"
                                                        placeholder="<html><body><h1>Hello {{user_name}}</h1></body></html>"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* ─── TAB: TESTING ─────────────────────────────────── */}
                            {activeTab === 'testing' && (
                                <div className="p-6 space-y-6 animate-in fade-in duration-200">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2.5">
                                            <Sliders className="w-4 h-4 text-(--primary)" />
                                            <div>
                                                <h3 className="text-base font-bold text-slate-900 dark:text-white">Test Payload</h3>
                                                <p className="text-xs text-slate-400 mt-0.5">Fill values to preview & trigger a test dispatch. <strong>Values are not saved.</strong></p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setShowTestModal(true)}
                                            disabled={!allMandatoryFilled || !isActive}
                                            className={`flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-lg transition-colors shadow-sm ${allMandatoryFilled && isActive
                                                ? 'bg-(--primary) hover:opacity-90 text-white cursor-pointer'
                                                : 'bg-slate-100 dark:bg-slate-800 text-slate-400 border border-slate-200 dark:border-slate-700 cursor-not-allowed'
                                                }`}
                                            title={!isActive ? 'Template must be enabled to test' : !allMandatoryFilled ? 'Fill all required fields first' : 'Send test email'}
                                        >
                                            <Send className="w-4 h-4" />
                                            Try Now
                                        </button>
                                    </div>

                                    {/* Event Payload Selector when triggerType === 'event' */}
                                    {triggerType === 'event' && (
                                        <div className="p-4 bg-sky-50/70 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-800 rounded-xl space-y-3">
                                            <div className="flex items-center justify-between flex-wrap gap-2">
                                                <div className="flex items-center gap-2">
                                                    <Zap className="w-4 h-4 text-amber-500" />
                                                    <h4 className="text-xs font-bold text-slate-900 dark:text-white">Platform Event Payload Tester</h4>
                                                </div>
                                                <span className="text-[10px] font-bold text-sky-700 dark:text-sky-300 bg-sky-100 dark:bg-sky-900 px-2.5 py-0.5 rounded-full">
                                                    Auto-flattens event payload as variables
                                                </span>
                                            </div>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                                Select a connected event trigger to load its sample schema variables into the test preview context.
                                            </p>
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Target Event:</label>
                                                <select
                                                    value={selectedTestingEventId}
                                                    onChange={e => {
                                                        const evtId = e.target.value;
                                                        setSelectedTestingEventId(evtId);
                                                        const foundEvt = availableEvents.find(item => item.event_id === evtId);
                                                        if (foundEvt && foundEvt.sample_payload) {
                                                            setTestVariableValues(prev => ({ ...prev, ...foundEvt.sample_payload }));
                                                            if (foundEvt.sample_payload.to) {
                                                                setTestRecipient(foundEvt.sample_payload.to);
                                                            }
                                                            toast.success(`Loaded schema payload for ${evtId}`);
                                                        }
                                                    }}
                                                    className="px-3 py-1.5 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white font-mono focus:outline-none focus:ring-2 focus:ring-(--primary)/30"
                                                >
                                                    <option value="">-- Select Platform Event --</option>
                                                    {(eventIds.length > 0 ? availableEvents.filter(e => eventIds.includes(e.event_id)) : availableEvents).map(e => (
                                                        <option key={e.event_id} value={e.event_id}>
                                                            {e.event_id} ({e.description})
                                                        </option>
                                                    ))}
                                                </select>
                                                {selectedTestingEventId && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const foundEvt = availableEvents.find(item => item.event_id === selectedTestingEventId);
                                                            if (foundEvt && foundEvt.sample_payload) {
                                                                setTestVariableValues(prev => ({ ...prev, ...foundEvt.sample_payload }));
                                                                if (foundEvt.sample_payload.to) {
                                                                    setTestRecipient(foundEvt.sample_payload.to);
                                                                }
                                                                toast.success(`Refreshed schema payload for ${selectedTestingEventId}`);
                                                            }
                                                        }}
                                                        className="px-2.5 py-1 text-xs font-semibold text-(--primary) bg-white dark:bg-slate-900 border border-sky-200 dark:border-sky-800 rounded-lg hover:bg-sky-50 transition-colors"
                                                    >
                                                        Reset Schema Defaults
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {!isActive && (
                                        <div className="p-3.5 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/50 rounded-xl flex items-center gap-2 text-xs text-amber-800 dark:text-amber-300 font-medium">
                                            <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
                                            Template is disabled — enable it on the General tab before sending test emails.
                                        </div>
                                    )}

                                    <div className="space-y-3">
                                        {/* Primary Recipient (to) */}
                                        <div className="p-3.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl">
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <span className="font-mono font-bold text-xs text-(--primary) w-28 shrink-0">
                                                    {`{{to}}`}
                                                </span>
                                                <div className="flex items-center gap-1.5 text-xs text-slate-500 flex-1 min-w-[200px]">
                                                    <span className="font-semibold shrink-0">Recipient Email:</span>
                                                    <input
                                                        type="email"
                                                        value={testRecipient}
                                                        onChange={e => setTestRecipient(e.target.value)}
                                                        className="px-2.5 py-1 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-800 dark:text-slate-200 flex-1 focus:outline-none focus:ring-1 focus:ring-(--primary)"
                                                        placeholder="user@example.com"
                                                        required
                                                    />
                                                </div>
                                                <div className="ml-auto flex items-center gap-2 shrink-0">
                                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400">
                                                        Required
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Other variables */}
                                        {allVars.filter(v => v.key !== 'to').map(v => (
                                            <div
                                                key={v.key}
                                                className="p-3.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
                                            >
                                                <div className="flex items-center gap-3 flex-wrap">
                                                    <span className="font-mono font-bold text-xs text-(--primary) w-28 shrink-0">
                                                        {`{{${v.key}}}`}
                                                    </span>
                                                    <div className="flex items-center gap-1.5 text-xs text-slate-500 flex-1 min-w-[200px]">
                                                        <span className="font-semibold shrink-0">Test Override:</span>
                                                        <input
                                                            type="text"
                                                            value={testVariableValues[v.key] ?? ''}
                                                            onChange={e => setTestVariableValues({ ...testVariableValues, [v.key]: e.target.value })}
                                                            className="px-2.5 py-1 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-800 dark:text-slate-200 flex-1 font-mono focus:outline-none focus:ring-1 focus:ring-(--primary)"
                                                            placeholder={v.default_value ? `Default: ${v.default_value}` : 'Empty'}
                                                        />
                                                    </div>
                                                    <div className="ml-auto flex items-center gap-2 shrink-0">
                                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${v.required ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400' : 'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400'}`}>
                                                            {v.required ? 'Required' : 'Optional'}
                                                        </span>
                                                        {systemVariables.some(sv => sv.key === v.key) && (
                                                            <span className="text-[10px] bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
                                                                <Lock className="w-2.5 h-2.5" /> Built-in
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {!allMandatoryFilled && (
                                        <div className="p-3.5 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/30 rounded-xl flex items-center gap-2 text-xs text-red-700 dark:text-red-400">
                                            <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                                            Fill all required (*) fields to enable the test dispatch.
                                        </div>
                                    )}

                                    {/* Inline preview hint */}
                                    <div className="border-t border-slate-100 dark:border-slate-800 pt-4">
                                        <p className="text-xs text-slate-400 mb-3 flex items-center gap-1.5">
                                            <Eye className="w-3.5 h-3.5" /> Live preview (rendered with current payload values)
                                        </p>
                                        <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden bg-white dark:bg-slate-950">
                                            <div className="px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800">
                                                <p className="text-sm font-bold text-slate-800 dark:text-white truncate">{renderedSubject || '(No Subject)'}</p>
                                                <p className="text-xs text-slate-400 font-mono mt-0.5">To: {testRecipient || 'recipient@example.com'}</p>
                                            </div>
                                            <div className="h-64">
                                                <iframe
                                                    title="Live Email Preview"
                                                    srcDoc={renderedHtml}
                                                    className="w-full h-full border-0 bg-white"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ─── TAB: API DOCS ────────────────────────────────── */}
                            {activeTab === 'api' && (
                                <div className="p-6 space-y-6 animate-in fade-in duration-200">
                                    <div className="flex items-center gap-2.5">
                                        <Zap className="w-4 h-4 text-(--primary)" />
                                        <div>
                                            <h3 className="text-base font-bold text-slate-900 dark:text-white">API Integration</h3>
                                            <p className="text-xs text-slate-400 mt-0.5">Use this endpoint to trigger the email template from your application</p>
                                        </div>
                                    </div>

                                    {/* Endpoint URL */}
                                    <div className="space-y-2">
                                        <h4 className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Invocation Endpoint</h4>
                                        <div className={`p-4 rounded-xl border flex flex-col sm:flex-row sm:items-center gap-3 ${isActive
                                            ? 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900/50'
                                            : 'bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800'
                                            }`}>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-[11px] font-bold bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-400 px-2 py-0.5 rounded font-mono">POST</span>
                                                    {isActive
                                                        ? <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400">Active</span>
                                                        : <span className="text-[11px] font-bold text-slate-400">Inactive</span>
                                                    }
                                                </div>
                                                <p className="font-mono text-xs text-slate-700 dark:text-slate-300 break-all select-all">{invocationUrl}</p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handleCopyUrl}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-lg transition-colors shrink-0"
                                            >
                                                {copiedUrl ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                                                {copiedUrl ? 'Copied!' : 'Copy URL'}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Request schema */}
                                    <div className="space-y-2">
                                        <h4 className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Request Body Schema</h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                            {[
                                                { field: 'to', type: 'string', desc: 'Primary recipient email address', required: true },
                                                { field: 'variables', type: 'object', desc: 'Key-value map of template variables', required: false },
                                                { field: 'service_config_id', type: 'string?', desc: 'Override service provider ID', required: false },
                                            ].map(f => (
                                                <div key={f.field} className="p-3 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="font-mono text-xs font-bold text-(--primary)">{f.field}</span>
                                                        {f.required && <span className="text-[10px] bg-red-100 text-red-600 dark:bg-red-950 dark:text-red-400 px-1.5 py-0.5 rounded font-bold">Required</span>}
                                                    </div>
                                                    <p className="text-[11px] text-slate-400 font-mono">{f.type}</p>
                                                    <p className="text-[11px] text-slate-500 mt-1">{f.desc}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* CURL snippet */}
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <h4 className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">CURL Example</h4>
                                            <button
                                                type="button"
                                                onClick={handleCopyCurl}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-(--primary) hover:bg-(--primary-hover) text-white rounded-lg transition-colors"
                                            >
                                                {copiedCurl ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                                {copiedCurl ? 'Copied!' : 'Copy CURL'}
                                            </button>
                                        </div>
                                        <pre className="p-5 bg-slate-950 text-slate-100 font-mono text-xs rounded-xl overflow-x-auto border border-slate-800 leading-relaxed select-all">
                                            {generateCurlSnippet()}
                                        </pre>
                                    </div>

                                    {/* Variables reference table */}
                                    {customVariables.length > 0 && (
                                        <div className="space-y-2">
                                            <h4 className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Custom Variables Reference</h4>
                                            <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
                                                <table className="w-full text-xs">
                                                    <thead>
                                                        <tr className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-800">
                                                            <th className="px-4 py-2.5 text-left font-bold text-slate-500 uppercase tracking-wider">Key</th>
                                                            <th className="px-4 py-2.5 text-left font-bold text-slate-500 uppercase tracking-wider">Label</th>
                                                            <th className="px-4 py-2.5 text-left font-bold text-slate-500 uppercase tracking-wider">Default</th>
                                                            <th className="px-4 py-2.5 text-left font-bold text-slate-500 uppercase tracking-wider">Required</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {customVariables.map((v, i) => (
                                                            <tr key={v.key} className={`border-b border-slate-100 dark:border-slate-800 last:border-0 ${i % 2 === 0 ? '' : 'bg-slate-50/50 dark:bg-slate-900/30'}`}>
                                                                <td className="px-4 py-2.5 font-mono text-(--primary) font-bold">{`{{${v.key}}}`}</td>
                                                                <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300">{v.label || '—'}</td>
                                                                <td className="px-4 py-2.5 text-slate-500 font-mono">{v.default_value || '—'}</td>
                                                                <td className="px-4 py-2.5">
                                                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${v.required ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400' : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'}`}>
                                                                        {v.required ? 'Yes' : 'No'}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    <div className="p-4 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl">
                                        <div className="flex items-center gap-2 mb-2">
                                            <ChevronRight className="w-3.5 h-3.5 text-(--primary)" />
                                            <h5 className="text-xs font-bold text-slate-700 dark:text-slate-300">Authentication</h5>
                                        </div>
                                        <p className="text-xs text-slate-500">
                                            All service endpoints require a valid <span className="font-mono bg-slate-200 dark:bg-slate-800 px-1 py-0.5 rounded">Authorization: Bearer &lt;token&gt;</span> header. Tokens are issued via the Argus IAM token endpoint.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── MODALS ──────────────────────────────────────────────────────── */}

            {/* Delete Confirmation */}
            <ConfirmDeleteModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title="Delete Email Template"
                itemName={template.name}
                description="Are you sure you want to delete this email template? Applications triggering this template ID will no longer be able to send emails."
            />

            {/* Test Send Modal */}
            {showTestModal && (
                <div className="fixed inset-0 bg-slate-900/50 dark:bg-black/85 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
                    <Card
                        className="w-full max-w-4xl shadow-2xl border-none flex flex-col max-h-[90vh]"
                        title={
                            <div className="flex items-center gap-2">
                                <Send className="w-5 h-5 text-(--primary)" />
                                <span>Confirm Dispatch — Mail Preview</span>
                            </div>
                        }
                        actions={
                            <button
                                type="button"
                                onClick={() => setShowTestModal(false)}
                                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg transition-colors cursor-pointer"
                                title="Close Preview"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        }
                        subtitle="Review the final rendered message before confirming execution."
                    >
                        <div className="py-4 overflow-y-auto max-h-[60vh] space-y-4">
                            <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden bg-white dark:bg-slate-955 flex flex-col h-[500px]">
                                {/* Email Header */}
                                <div className="p-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/90 space-y-2">
                                    <h3 className="text-base font-bold text-slate-900 dark:text-white truncate">
                                        Subject: {renderedSubject || '(No Subject)'}
                                    </h3>
                                    <div className="space-y-1 text-xs">
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-slate-400 w-12 uppercase">To:</span>
                                            <span className="font-mono text-slate-900 dark:text-white bg-slate-200/60 dark:bg-slate-800 px-2 py-0.5 rounded">
                                                {testRecipient || 'recipient@example.com'}
                                            </span>
                                        </div>
                                        {testVariableValues['cc']?.trim() && (
                                            <div className="flex items-center gap-2">
                                                <span className="font-bold text-slate-400 w-12 uppercase">Cc:</span>
                                                <span className="font-mono text-slate-600 dark:text-slate-300">{testVariableValues['cc']}</span>
                                            </div>
                                        )}
                                        {testVariableValues['bcc']?.trim() && (
                                            <div className="flex items-center gap-2">
                                                <span className="font-bold text-slate-400 w-12 uppercase">Bcc:</span>
                                                <span className="font-mono text-slate-600 dark:text-slate-300">{testVariableValues['bcc']}</span>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-slate-400 w-12">Date:</span>
                                            <span className="text-slate-500">
                                                {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                {/* Body */}
                                <div className="flex-1 w-full flex flex-col bg-white">
                                    <iframe
                                        title="Email Body Preview"
                                        srcDoc={renderedHtml}
                                        className="w-full flex-1 border-0 bg-white"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                            <Button variant="ghost" className="flex-1" onClick={() => setShowTestModal(false)} disabled={isSendingTest}>
                                Cancel
                            </Button>
                            <Button
                                className="flex-1 bg-(--primary) hover:opacity-90 text-white font-bold"
                                onClick={handleSendTest}
                                isLoading={isSendingTest}
                            >
                                Confirm & Send Test Email
                            </Button>
                        </div>
                    </Card>
                </div>
            )}
        </PageLayout>
    );
};
